import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalViews } from '../../../constants';
import { StockCountItem, StockInventoryCountUpdatedItems } from '../../../hooks';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Button, Modal } from '../../../stories/molecules';
import { muiTheme } from '../../../theme';
import { commonTranslations, yearlyStockTakeTranslations } from '../../../translations';
import { StateView } from '../stateView/StateView';
import { StyledNote } from '../YearlyStockTakePage.styles';
import { MultipleStockView } from './MultipleStockView';

type ItemSelectionModalProps = {
  isOpen: boolean;
  stockModalDetails: StockCountItem[];
  modalView: ModalViews;
  isLoading: boolean;
  onClose: () => void;
  updateStock: (updatedItems: StockInventoryCountUpdatedItems[]) => void;
  removeItem: (item: StockCountItem) => void;
};

export const ItemSelectionModal: FC<ItemSelectionModalProps> = ({
  isOpen,
  stockModalDetails,
  modalView,
  isLoading,
  onClose,
  updateStock,
  removeItem,
}) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('tabletLandscape'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const { noteText, bulkAction: bulkActionTranslations } = yearlyStockTakeTranslations;

  const pickedItems = stockModalDetails.filter((item) => item.status === 'Pick');
  const unpickedItems = stockModalDetails.filter((item) => item.status === 'Picked');

  const handleMultipleUpdates = (updatedStockItems: StockCountItem[]) => {
    const updatedItems: StockInventoryCountUpdatedItems[] = updatedStockItems.map((item) => ({
      sicScope: item.sicScope,
      status: item.status === 'Pick' ? 'Picked' : 'Pick',
    }));
    updateStock(updatedItems);
  };

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <ColumnFlexContainer
        data-testid="multiple-action-modal"
        minWidth={isDesktop ? [150] : ''}
        padding={isMobile ? [4] : isTablet ? [8, 10] : [8]}
      >
        {modalView === ModalViews.INITIAL && (
          <ColumnFlexContainer padding={isMobile ? [6, 0] : [0]} data-testid="initial-view">
            <RowFlexContainer margin={[0, 0, 4]}>
              <Icon icon={Icons.SCAN} margin={[0, 2, 0, 0]} size={isMobile ? 'small' : 'large'} />
              <Typography type="h4" textAlign="center">
                {t(bulkActionTranslations.modalTitle.key, bulkActionTranslations.modalTitle.defaultValue)}
              </Typography>
            </RowFlexContainer>
            <StyledNote>
              <Icon icon={Icons.WARNING} margin={[0, 2, 0, 0]} size="small" />
              <Typography>{t(noteText.key, noteText.defaultValue)}</Typography>
            </StyledNote>

            <MultipleStockView
              pickedItems={pickedItems}
              unpickedItems={unpickedItems}
              modalView={modalView}
              removeItem={(item) => removeItem(item)}
            />
            <RowFlexContainer flex={1} gap={[2]} padding={[3, 0, 0]}>
              <Button fullWidth variant="secondary" label="Cancel" onClick={onClose} />
              <Button
                data-testid="confirm-button"
                fullWidth
                label={t(commonTranslations.confirm.key, commonTranslations.confirm.defaultValue)}
                disabled={!pickedItems.length && !unpickedItems.length}
                isLoading={isLoading}
                onClick={() => handleMultipleUpdates([...pickedItems, ...unpickedItems])}
              />
            </RowFlexContainer>
          </ColumnFlexContainer>
        )}

        {modalView === ModalViews.SUCCESS && (
          <StateView
            data-testid="success-view"
            stockItems={
              <MultipleStockView pickedItems={pickedItems} unpickedItems={unpickedItems} modalView={modalView} />
            }
            color="validationGreen"
            title={t(commonTranslations.successText.key, commonTranslations.successText.defaultValue)}
            icon={Icons.CHECK_MARK_CIRCLE}
            subTitle={t(bulkActionTranslations.successText.key, bulkActionTranslations.successText.defaultValue)}
            onClose={onClose}
          />
        )}

        {modalView === ModalViews.ERROR && (
          <StateView
            data-testid="error-view"
            stockItems={
              <MultipleStockView pickedItems={pickedItems} unpickedItems={unpickedItems} modalView={modalView} />
            }
            color="red"
            title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
            icon={Icons.REFRESH}
            subTitle={t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
            onClose={onClose}
          />
        )}
      </ColumnFlexContainer>
    </Modal>
  );
};
